import GoogleAdsLogo from '@/assets/google-ads-logo.svg';
import MetaLogo from '@/assets/meta-logo.svg';
import { FEATURE_FLAGS } from '@/utils/featureFlags';
import { RecruitmentMarketingProvider } from '@/utils/types';

import type { ConnectionScope } from './ConnectionStatusCard';

/**
 * What the settings modal asks for once an account is picked. Meta scopes a
 * connection to a single pixel; Google has no pixel equivalent to choose, and
 * instead maps the connection to the conversion events to send back.
 */
export type ProviderSettingsField = 'pixel' | 'events';

export interface RecruitmentMarketingProviderDetails {
  provider: RecruitmentMarketingProvider;
  /** Full product name ("Meta Ads"), used as the card title. */
  label: string;
  /** The platform alone ("Meta"), for copy like "…sent to Meta stops". */
  shortName: string;
  /**
   * What the account a connection is scoped to is called. Both platforms use
   * "business account" today; it stays per-provider so either can diverge.
   * Cased for mid-sentence use — the modal capitalises it for the field label.
   */
  accountLabel: string;
  /**
   * What one connection covers: Meta authorises once for the whole company and
   * every panel reuses it, Google once per panel.
   */
  scope: ConnectionScope;
  /** Sub-heading under the settings modal's title. */
  modalDescription: string;
  settingsField: ProviderSettingsField;
  logo: string;
  /** Absent for Meta, which shipped ungated. */
  featureFlag?: string;
}

/**
 * The conversion events a connection can send back, in display order. Wording
 * matches the campaign wizard's Step 4 switches, which gate the same two
 * events at campaign level.
 *
 * Each enabled event carries the id of the Google conversion action it maps
 * to, under its own `payloadKey` in `provider_config.google`. The spec's
 * `anyOf` requires at least one of those keys, which is what makes "at least
 * one event" a hard requirement rather than a UI nicety.
 */
export const CONVERSION_EVENTS = [
  {
    id: 'double_opt_in',
    label: 'Double opt-in',
    tooltip:
      "A double opt-in happens when a panelist confirms their registration a second time, for example through a confirmation email, before they're counted as fully registered.",
    payloadKey: 'double_opt_in_external_id',
  },
  {
    id: 'complete_per_panelist',
    label: 'Complete per panelist',
    tooltip:
      "A survey complete happens when a panelist finishes a survey and qualifies. Partial completes, screen outs, and quota cuts don't count.",
    payloadKey: 'completion_external_id',
  },
] as const;

export type ConversionEventId = (typeof CONVERSION_EVENTS)[number]['id'];

export const RECRUITMENT_MARKETING_PROVIDER_DETAILS: Record<
  RecruitmentMarketingProvider,
  RecruitmentMarketingProviderDetails
> = {
  meta: {
    provider: 'meta',
    label: 'Meta Ads',
    shortName: 'Meta',
    accountLabel: 'business account',
    scope: 'company',
    modalDescription: 'Please confirm your business account and Pixel settings',
    settingsField: 'pixel',
    logo: MetaLogo,
  },
  google: {
    provider: 'google',
    label: 'Google Ads',
    shortName: 'Google',
    accountLabel: 'business account',
    scope: 'panel',
    modalDescription:
      'Verify your business account and link your conversion actions to the events to start tracking performance.',
    settingsField: 'events',
    logo: GoogleAdsLogo,
    featureFlag: FEATURE_FLAGS.IS_GOOGLE_MARKETING_SIGNALS_ENABLED,
  },
};

/** Display order of the integrations. Callers filter this by `featureFlag`. */
export const RECRUITMENT_MARKETING_PROVIDERS: RecruitmentMarketingProviderDetails[] =
  [
    RECRUITMENT_MARKETING_PROVIDER_DETAILS.meta,
    RECRUITMENT_MARKETING_PROVIDER_DETAILS.google,
  ];
